import { type VehicleType, UscContext } from "../../shared-logic/types/UscCommonTypes";
import {
    type AEMTrackingDataLayerType,
    type AEMTrackingProductType,
    type AEMTrackingShortEventType,
    getDealerAEMTrackingInfo,
    trackEvent,
    updateTrackingBaseLayer,
} from "../../../common-deprecated/utils/tracking";
import { type PdpSection, DealerInfoType, UscNavStepEnum } from "./constants";
import type { DealerResultType } from "../../../common-deprecated/utils/dealerConstants";
import type { FinanceOptionType } from "../../../common-deprecated/types/CommonTypes";
import type { CarDeliveryDestination, CarDeliveryTransportationOption, UscCarDeliveryConfig } from "./carDeliveryUtils";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import type { UscVehicleType } from "../../shared-logic/utils/uscUtils";
import { getVehicleFullName } from "./helpers";
import { getUscCashPriceFromVehicle, getUscCashPriceValue } from "../../shared-logic/utils/uscPriceUtils";

// The usc tracking location defines where an action is triggered related to tracking
// This is as edge case also used to hide the contact dealer button on desktop
export enum UscTrackingLocation {
    Pdp = "detail-page", // General pdp step
    Extras = "customize-page", // General extras step
    Basket = "basket", // Basket on pdp or extras step
    BasketDetails = "summary-overlay", // basket details on pdp or extras step
    MobileNavModal = "cta-overlay",
    Footer = "footer", // buttons in all sticky footers
}

export const uscStepTrackingLocationMap: Record<UscNavStepEnum, UscTrackingLocation> = {
    [UscNavStepEnum.Pdp]: UscTrackingLocation.Pdp,
    [UscNavStepEnum.Extras]: UscTrackingLocation.Extras,
};

export const getCarDeliveryAEMTrackingInfo = (
    config: UscCarDeliveryConfig,
    commonSettings: CommonSettingsType,
): AEMTrackingDataLayerType["delivery"] => ({
    products: [
        {
            productname: "delivery",
            price: String(config.price.cashPriceInclVat),
            currencyCode: commonSettings.intl.currencyCode,
        },
    ],
    settings: [
        {
            calculationname: "deliveryType",
            currentvalue: config.destination,
        },
        {
            calculationname: "transportType",
            currentvalue: config.transportationType,
        },
    ],
});

export const getTrackingPrefix = (context: UscContext): string => (context === UscContext.Used ? "uc" : "sc");

/**
 * Retrieve a productInfo object based on a used/stock vehicle.
 * Used in both USC and UscForm tracking.
 */
export const getAemVehicleProductInfo = (vehicle: UscVehicleType): AEMTrackingProductType["productInfo"] => {
    return {
        productId: vehicle.id,
        productName: getVehicleFullName(vehicle),
        price: getUscCashPriceValue(getUscCashPriceFromVehicle(vehicle)).toFixed(2),
        currencyCode: vehicle.currencyCode,
        modelName: vehicle.product.model,
        modelVersion: vehicle.product.versionName,
        engineType: vehicle.product.engine.marketingFuelType.description,
        manufacturerId: vehicle.product.brand.code,
        manufacturerName: vehicle.product.brand.description,
        licensePlate: vehicle.licensePlate,
        ecoLabel: vehicle.product.eco?.label?.description,
    };
};

export const setupTrackingBaseLayerAem = (
    context: UscContext,
    result: VehicleType,
    dealer: DealerResultType | null,
): void => {
    updateTrackingBaseLayer({
        product: [
            {
                category: "cars",
                subCategory: `${context}-cars`,
                productInfo: getAemVehicleProductInfo(result),
            },
        ],
        dealer: dealer ? getDealerAEMTrackingInfo(dealer) : undefined,
    });
};

export const trackAemDealerSeeCarsLink = (context: UscContext, dealer: DealerResultType): void =>
    trackEvent(
        {
            component: `${getTrackingPrefix(context)}-detail-page`,
            group: `${context}-cars`,
            name: "componentevent",
            action: "see-all-cars",
            label: dealer.name,
        },
        { dealer: getDealerAEMTrackingInfo(dealer) },
    );

export const trackAccessorySliderClick = (context: UscContext): void => {
    trackEvent({
        component: `${getTrackingPrefix(context)}-detail-page`,
        group: `${context === UscContext.Used ? "used" : "stock"}-cars`,
        name: "componentevent",
        action: "slide",
        label: "vehicleForSaleId",
    });
};

export const trackCarDeliveryMoreInfoClick = (context: UscContext): void =>
    trackEvent({
        component: `${getTrackingPrefix(context)}-delivery-popin`,
        group: `${context === UscContext.Used ? "used" : "stock"}-cars`,
        name: "componentevent",
        action: "click-delivery-popin",
        label: "more-info",
    });

export const trackCarDeliveryCloseInfoModal = (context: UscContext): void =>
    trackEvent({
        component: `${getTrackingPrefix(context)}-delivery-popin`,
        group: `${context === UscContext.Used ? "used" : "stock"}-cars`,
        name: "componentevent",
        action: "click-delivery-popin",
        label: "close-popin",
    });

export const trackCarDeliveryDiscoverMoreClick = (context: UscContext): void =>
    trackEvent({
        component: `${getTrackingPrefix(context)}-customize-page`,
        group: `${context === UscContext.Used ? "used" : "stock"}-cars`,
        name: "componentevent",
        action: "click-link",
        label: "car-delivery-infos",
    });

export const trackCarDeliveryStepDestination = (context: UscContext): void =>
    trackEvent(
        {
            component: `${getTrackingPrefix(context)}-delivery-calculator`,
            group: `${context === UscContext.Used ? "used" : "stock"}-cars`,
            name: "workflowevent",
            action: "delivery-workflow",
            label: "configure-destination",
        },
        {
            workflow: {
                name: `${getTrackingPrefix(context)}-delivery-calculator`,
                step: "configure-destination",
                conversion: 0,
            },
        },
    );

export const trackCarDeliveryStepTransportation = (context: UscContext): void =>
    trackEvent(
        {
            component: `${getTrackingPrefix(context)}-delivery-calculator`,
            group: `${context === UscContext.Used ? "used" : "stock"}-cars`,
            name: "workflowevent",
            action: "delivery-workflow",
            label: "configure-transportation",
        },
        {
            workflow: {
                name: `${getTrackingPrefix(context)}-delivery-calculator`,
                step: "configure-transportation",
                conversion: 0,
            },
        },
    );

export const trackCarDeliverySelectDeliveryType = (context: UscContext, deliveryType: CarDeliveryDestination): void =>
    trackEvent({
        component: `${getTrackingPrefix(context)}-delivery-calculator`,
        group: `${context === UscContext.Used ? "used" : "stock"}-cars`,
        name: "componentevent",
        action: "delivery-action",
        label: `select-delivery-${deliveryType}`,
    });

export const trackCarDeliverySelectTransportationType = (
    context: UscContext,
    transportationType: CarDeliveryTransportationOption,
): void =>
    trackEvent({
        component: `${getTrackingPrefix(context)}-delivery-calculator`,
        group: `${context === UscContext.Used ? "used" : "stock"}-cars`,
        name: "componentevent",
        action: "delivery-action",
        label: `select-transport-${transportationType}`,
    });

export const trackCarDeliveryConfirmDelivery = (
    context: UscContext,
    deliveryConfig: UscCarDeliveryConfig,
    commonSettings: CommonSettingsType,
): void =>
    trackEvent(
        {
            component: `${getTrackingPrefix(context)}-delivery-calculator`,
            group: `${context === UscContext.Used ? "used" : "stock"}-cars`,
            name: "deliveryevent",
            action: "delivery-workflow",
            label: "delivery-confirmed",
        },
        {
            delivery: getCarDeliveryAEMTrackingInfo(deliveryConfig, commonSettings),
            workflow: {
                name: `${getTrackingPrefix(context)}-delivery-calculator`,
                step: "delivery-confirmed",
                conversion: 1,
            },
            product: [
                ...(window.tngaDataLayer?.product || []),
                {
                    category: "services",
                    subCategory: "delivery",
                    productInfo: {
                        productName: "delivery",
                        price: String(deliveryConfig.price.cashPriceInclVat),
                        currencyCode: commonSettings.intl.currencyCode,
                    },
                },
            ],
        },
    );

export const getAemTrackingDefaults = (
    context: UscContext,
    location: UscTrackingLocation,
): Omit<AEMTrackingShortEventType, "action"> => {
    return {
        component: `${getTrackingPrefix(context)}-${location}`,
        group: `${context}-cars`,
    };
};

export const trackUscWorkloadEvent = (stepLabel: string, conversion: number, context: UscContext): void => {
    trackEvent(
        {
            name: "workflowevent",
            component: `${getTrackingPrefix(context)}-configurator`,
            group: `${context}-cars`,
            action: "workflow-step",
            label: `${getTrackingPrefix(context)}-${stepLabel}`,
        },
        {
            workflow: {
                name: `${getTrackingPrefix(context)}-configurator`,
                step: `${getTrackingPrefix(context)}-${stepLabel}`,
                conversion,
                variant: "uc-pdp-v2",
            },
        },
    );
};

export const trackFinanceToggle = (
    location: UscTrackingLocation,
    financeOption: FinanceOptionType,
    context: UscContext,
): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, location),
        action: `click-${getTrackingPrefix(context)}-panel`,
        label: `change-price-display-${financeOption}`,
    });
};

export const trackStepLoad = (step: UscNavStepEnum, context: UscContext): void => {
    const trackingStepValueMap = {
        [UscNavStepEnum.Pdp]: "detail-page",
        [UscNavStepEnum.Extras]: "customize-page",
    };
    const conversion = step === UscNavStepEnum.Extras ? 1 : 0;
    const stepLabel = trackingStepValueMap[step];

    trackUscWorkloadEvent(stepLabel, conversion, context);
};

export const trackOpenBasketDetailsModal = (location: UscTrackingLocation, context: UscContext): void => {
    trackCta("summary-page", location, context);
    trackUscWorkloadEvent("summary-page", 0, context);
};

export const trackCarouselOpenFullScreen = (context: UscContext): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.Pdp),
        name: "componentevent",
        action: "click-gallery-fullscreen",
    });
};

export const trackBrowseCarousel = (context: UscContext, filename?: string): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.Pdp),
        name: "componentevent",
        action: "browse-gallery",
        label: filename,
    });
};

export const trackClickMenuSection = (context: UscContext, section: PdpSection): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.Pdp),
        name: "componentevent",
        action: "click-anchor",
        label: section,
    });
};

export const trackAccessoryReadMoreClick = (context: UscContext, accessoryName: string): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.Pdp),
        name: "componentevent",
        action: "click-accessories",
        label: `detail-${accessoryName}`,
    });
};

export const trackPackShowFeaturesClick = (context: UscContext, packName: string): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.Pdp),
        name: "componentevent",
        action: "click-packs",
        label: `detail-${packName}`,
    });
};

export const trackCta = (
    partialValue: string,
    location: UscTrackingLocation,
    context: UscContext,
    includeTrackingPrefix: boolean = true,
): void => {
    const label = includeTrackingPrefix ? `${getTrackingPrefix(context)}-${partialValue}` : partialValue;

    trackEvent({
        ...getAemTrackingDefaults(context, location),
        name: "ctaevent",
        action: "click-cta",
        label,
    });
};

// Similar to trackCta, but prefix is different
export const trackReserveCheckoutCta = (
    location: UscTrackingLocation,
    activeFinanceOption: FinanceOptionType,
    context: UscContext,
): void => {
    const trackContext = context === UscContext.Used ? "usedcars" : "stockcars";
    const value = `${trackContext}-online-retailing-checkout-${activeFinanceOption}`;

    trackEvent({
        ...getAemTrackingDefaults(context, location),
        name: "ctaevent",
        action: "click-cta",
        label: value,
    });
};

export const trackBasketRemoveItem = (location: UscTrackingLocation, context: UscContext, label: string): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, location),
        name: "componentevent",
        action: "remove-from-basket",
        label,
    });
};

export const trackAemDealerMapsLink = (context: UscContext, dealer: DealerResultType): void =>
    trackEvent(
        {
            ...getAemTrackingDefaults(context, UscTrackingLocation.Pdp),
            name: "dealerevent",
            action: "get-direction-start",
            label: dealer.name,
        },
        {
            dealer: getDealerAEMTrackingInfo(dealer),
        },
    );

export const trackAemContactDealerCtas = (
    type: DealerInfoType,
    dealer: DealerResultType,
    context: UscContext,
): void => {
    const actionMap: Record<DealerInfoType, string> = {
        [DealerInfoType.PhoneNumber]: "call-dealer",
        [DealerInfoType.Email]: "email-dealer",
        [DealerInfoType.Website]: "open-dealersite",
        [DealerInfoType.Impressum]: "print-dealer-details",
    };

    trackEvent(
        {
            ...getAemTrackingDefaults(context, UscTrackingLocation.Pdp),
            name: "dealerevent",
            action: actionMap[type],
            label: dealer.name,
        },
        {
            dealer: getDealerAEMTrackingInfo(dealer),
        },
    );
};

export const trackAemCustomerSupportClick = (context: UscContext): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.Extras),
        name: "ctaevent",
        action: "contact-call-center-phone",
        label: "contact-call-center-phone",
    });
};

export const trackCalculateTaxes = (
    context: UscContext,
    location: UscTrackingLocation,
    selectedRegion: string,
): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, location),
        name: "componentevent",
        action: "calculate-taxes",
        label: selectedRegion,
    });
};

export const trackDiscoverYourCarModalOpen = (context: UscContext): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.Footer),
        name: "componentevent",
        action: "open-section-menu",
    });
};
export const trackDiscoverYourCarModalClose = (context: UscContext): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.Footer),
        name: "componentevent",
        action: "close-section-menu",
    });
};

export const trackDiscoverYourCarModalSelect = (context: UscContext, sectionName: PdpSection): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.Footer),
        name: "componentevent",
        action: "click-section-menu",
        label: sectionName,
    });
};

export const trackCloseMobileNavModal = (context: UscContext): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, UscTrackingLocation.MobileNavModal),
        name: "componentevent",
        action: "close-cta-overlay",
    });
};

export const trackClickPdf = (location: UscTrackingLocation, context: UscContext): void => {
    trackEvent({
        ...getAemTrackingDefaults(context, location),
        action: "click-print",
        label: window.location.href,
    });
};
